import { useCallback } from 'react';

import type { TLTenantProperty } from '@/api/gen';
import { TLPropertyType } from '@/api/gen';
import { useAppConfiguration } from '@/context/AppConfigurationContext';

type FeatureFlagValue = TLTenantProperty['value'] | string[];

/**
 * This hook allows components to retrieve feature flag values that are defined
 * in the current tenant's configuration.
 *
 * Usage:
 * // Get a single flag value directly
 * const isFeatureFlagEnabled = useFeatureFlag('FEATURE_FLAG_KEY', false);
 *
 * // Get a getter function for dynamic lookups
 * const getFeatureFlag = useFeatureFlag();
 *
 * @param key - Optional feature flag key to look up
 * @param defaultValue - Optional default value to return if the feature flag is not found
 * @returns Either the feature flag value or a getter function
 */
export function useFeatureFlag<T extends FeatureFlagValue>(key: string, defaultValue: T): T;
export function useFeatureFlag(): <T extends FeatureFlagValue>(key: string, defaultValue: T) => T;
export function useFeatureFlag<T extends FeatureFlagValue>(key?: string, defaultValue?: T) {
  const { selectedTenant } = useAppConfiguration();

  const getFeatureFlag = useCallback(
    <V extends FeatureFlagValue>(flagKey: string, fallbackValue: V): V => {
      const normalizedFlagKey = flagKey.toLowerCase();
      const featureFlag = selectedTenant?.configuration?.find(
        (config) => config.key.toLowerCase() === normalizedFlagKey,
      );
      if (!featureFlag) return fallbackValue;
      if (featureFlag.type === TLPropertyType.list && typeof featureFlag.value === 'string') {
        return featureFlag.value.split(',') as V;
      }

      return featureFlag.value as unknown as V;
    },
    [selectedTenant?.configuration],
  );

  if (key !== undefined && defaultValue !== undefined) {
    return getFeatureFlag(key, defaultValue);
  }

  return getFeatureFlag;
}
